import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { hasModuleAccess } from '../utils/permissions';

const RoleProtectedRoute = ({ children, requiredModule = null }) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const checkAuthorization = async () => {
      const user = auth.currentUser;
      
      if (!user) {
        setIsAuthorized(false);
        setLoading(false);
        return;
      }

      try {
        // First, try to get user document by document ID (matching auth UID)
        let userDoc = await getDoc(doc(db, 'users', user.uid));
        
        // If document doesn't exist with that ID, try querying by uid field
        if (!userDoc.exists()) {
          const usersRef = collection(db, 'users');
          const uidQuery = query(usersRef, where('uid', '==', user.uid), limit(1));
          const querySnapshot = await getDocs(uidQuery);
          
          if (!querySnapshot.empty) {
            // Found user document with uid field matching auth UID
            userDoc = querySnapshot.docs[0];
          }
        }
        
        if (!userDoc.exists()) {
          setIsAuthorized(false);
          setLoading(false);
          return;
        }

        const userRole = userDoc.data().role;
        
        // If no specific module is required, just check if user is authenticated
        if (!requiredModule) {
          setIsAuthorized(true);
        } else {
          // Check if user has access to the required module
          const hasAccess = hasModuleAccess(userRole, requiredModule);
          setIsAuthorized(hasAccess);
        }
      } catch (error) {
        console.error('Error checking authorization:', error);
        setIsAuthorized(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuthorization();
  }, [requiredModule, location.pathname]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthorized) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default RoleProtectedRoute;

